import { api } from './client';

export interface ShopifyConfig {
  id: number;
  storeDomain: string | null;
  apiVersion: string | null;
  locationId: string | null;
  dryRun: boolean;
  safetyStock: number;
  orderSyncEnabled: boolean;
  stockSyncEnabled: boolean;
  orderWriteback: boolean;
  orderSinceCursor: string | null;
  lastOrderSyncAt: string | null;
  lastStockSyncAt: string | null;
  lastCatalogSyncAt: string | null;
  tokenSet: boolean;
}

export interface ShopifyLocation { id: string; name: string; isActive: boolean }
export interface ShopifyConnResult { ok: boolean; shopName: string; domain: string; locations: ShopifyLocation[] }
export interface CatalogSummary { variants: number; mappable: number; mapped: number; matchedByBarcode: number; unmapped: number; blankSku: number; duplicates: number; untracked: number }

export interface SkuMapRow {
  id: number; sku: string; shopifyBarcode: string; shopifyTitle: string; tracked: boolean;
  itemId: number | null; itemCode: string; itemName: string; lastPushedQty: number | null; mapped: boolean;
}
export interface SkuMapResponse {
  data: SkuMapRow[];
  summary: { total: number; mapped: number; unmapped: number; untracked: number };
}
export interface ShopifyLogRow { id: string; job: string; level: string; message: string; ref: string | null; createdAt: string }

export interface ShopifyOrderRow {
  id: number;
  shopifyOrderId: string;
  orderName: string | null;
  shopifyCreatedAt: string | null;
  financialStatus: string | null;
  /** Cash on Delivery or Prepaid (from Shopify's payment gateway), and the cash still to collect. */
  paymentMethod?: 'COD' | 'Prepaid';
  codAmount?: number | null;
  paymentGateways?: string[];
  fulfillmentStatus: string | null;
  currency: string | null;
  totalPrice: string | null;
  customerName: string | null;
  customerPhone: string | null;
  shipCity: string | null;
  erpStatus: string;
  cancelledAt: string | null;
  cancelReason: string | null;
  heldAt: string | null;
  holdReason: string | null;
  lineCount: number;
  /** Processed to the ERP but not yet on the Pick List — waiting for these items. */
  waitingForStock?: boolean;
  waitingFor?: StockShort[];
}
export interface ShopifyOrderLineDetail {
  id: number; sku: string | null; title: string | null; variantTitle: string | null;
  quantity: number; price: string | null; itemId: number | null;
  itemCode: string; itemName: string; mapped: boolean;
}
export interface ShopifyOrderDetail {
  id: number; shopifyOrderId: string; orderName: string | null;
  shopifyCreatedAt: string | null; processedAt: string | null;
  financialStatus: string | null; fulfillmentStatus: string | null; currency: string | null;
  subtotal: string | null; totalTax: string | null; totalDiscount: string | null; totalPrice: string | null;
  customerName: string | null; customerEmail: string | null; customerPhone: string | null;
  shipName: string | null; shipPhone: string | null; shipAddress1: string | null; shipAddress2: string | null;
  shipCity: string | null; shipProvince: string | null; shipZip: string | null; shipCountry: string | null;
  note: string | null; tags: string | null; erpStatus: string; erpEnquiryId: number | null;
  cancelledAt: string | null; cancelReason: string | null;
  heldAt: string | null; holdReason: string | null;
  erpEnqNo: string | null; erpEnqStage: string | null; syncedAt: string;
  /** Items still short while the order waits for the Pick List. */
  waitingFor?: StockShort[];
  /** Cash on Delivery or Prepaid (from Shopify's payment gateway), and the cash still to collect. */
  paymentMethod?: 'COD' | 'Prepaid';
  codAmount?: number | null;
  paymentGateways?: string[];
  lines: ShopifyOrderLineDetail[];
}
export interface PullOrdersResult { pulled: number; updated?: number; startPointSet?: string; upTo?: string }
/** An item an online order is waiting on: needed vs available (on hand − reserved). */
export interface StockShort { code: string; name: string; need: number; available: number }
export interface ProcessOrderResult { enqId: number; enqNo: string; pickListCreated: boolean; waitingFor: StockShort[] }
export interface RetryPickListResult { pickListCreated: boolean; enqNo: string; waitingFor: StockShort[]; reason: string | null }
export interface ResolveSkuResult { sku: string; itemId: number; itemCode: string; itemName: string; linesBackfilled: number; processedWithOtherItem?: string[] }

export interface StockRow {
  sku: string; variantName: string; itemId: number; itemCode: string; itemName: string; imageUrl: string | null; inventoryItemId: string;
  currentQty: number; reservedQty: number; safetyStock: number; safetyOverride: number | null;
  sellable: number; lastPushedQty: number | null; delta: number; willPush: boolean;
}
export interface StockPreview {
  dryRun: boolean; stockSyncEnabled: boolean; locationId: string | null; safetyStock: number;
  lastStockSyncAt: string | null; total: number; toPush: number; rows: StockRow[];
}
export interface StockPushResult { dryRun: boolean; candidates: number; pushed: number; errors: number; sample: { sku: string; from: number | null; to: number }[] }

export interface UnmappedRow {
  id: number; sku: string; shopifyBarcode: string; shopifyTitle: string;
  suggestedItemId: number | null; suggestedCode: string; suggestedName: string;
  suggestMethod: 'barcode' | 'code' | 'name' | null;
}
export interface UnmappedResult { total: number; count: number; withSuggestion: number; rows: UnmappedRow[] }
export interface BulkResolveResult { applied: number; linesBackfilled: number; errors: string[]; processedWithOtherItem?: string[] }

export async function getShopifyConfig() {
  return (await api.get<ShopifyConfig>('/shopify/config')).data;
}
export async function updateShopifyConfig(payload: Partial<Omit<ShopifyConfig, 'id' | 'tokenSet' | 'orderSinceCursor' | 'lastOrderSyncAt' | 'lastStockSyncAt'>>) {
  return (await api.patch<ShopifyConfig>('/shopify/config', payload)).data;
}
export async function testShopifyConnection() {
  return (await api.post<ShopifyConnResult>('/shopify/test-connection')).data;
}
export async function syncShopifyCatalog() {
  return (await api.post<CatalogSummary>('/shopify/sync-catalog', {}, { timeout: 300000 })).data;
}
export async function getShopifySkuMap(status?: string) {
  return (await api.get<SkuMapResponse>('/shopify/sku-map', { params: status ? { status } : {} })).data;
}
export async function getShopifyLogs() {
  return (await api.get<ShopifyLogRow[]>('/shopify/logs')).data;
}

// ── Online Orders (Phase 2) ──────────────────────────────────────────────────
export type ShopifyOrderBucket = 'received' | 'processing' | 'done' | 'ignored' | 'held' | 'cancelled';
/** One live-order bucket per order, precedence: cancelled → held → erpStatus. */
export function shopifyOrderBucket(o: ShopifyOrderRow): ShopifyOrderBucket {
  if (o.cancelledAt || o.erpStatus === 'cancelled') return 'cancelled';
  if (o.heldAt) return 'held';
  return (o.erpStatus as 'received' | 'processing' | 'done' | 'ignored') ?? 'received';
}
/** from / to: order-date window (YYYY-MM-DD, Indian calendar days). */
export async function getShopifyOrders(params: { status?: string; search?: string; from?: string; to?: string; all?: 1 } = {}) {
  return (await api.get<ShopifyOrderRow[]>('/shopify/orders', { params })).data;
}
export async function getShopifyOrder(id: number) {
  return (await api.get<ShopifyOrderDetail>(`/shopify/orders/${id}`)).data;
}
export async function pullShopifyOrders() {
  return (await api.post<PullOrdersResult>('/shopify/pull-orders', {}, { timeout: 180000 })).data;
}
export async function setShopifyOrderStartPoint(since: string) {
  return (await api.post<{ orderSinceCursor: string }>('/shopify/orders/set-start-point', { since })).data;
}
export async function processShopifyOrder(id: number) {
  return (await api.post<ProcessOrderResult>(`/shopify/orders/${id}/process`)).data;
}
export async function cancelShopifyOrder(
  id: number,
  payload: { reason?: string; note?: string; refund?: boolean; restock?: boolean; notifyCustomer?: boolean } = {},
) {
  return (await api.post<{ ok: true }>(`/shopify/orders/${id}/cancel`, payload)).data;
}
export interface TrackingCatchUp { checked: number; pushed: number; already: number; skipped: number; failed: number; off: boolean }
/** One-time: send every live Delhivery AWB that Shopify doesn't have yet. */
export async function pushShopifyTracking() {
  return (await api.post<TrackingCatchUp>('/shopify/orders/push-tracking')).data;
}
export async function holdShopifyOrder(id: number, reason: string) {
  return (await api.post(`/shopify/orders/${id}/hold`, { reason })).data;
}
export async function unholdShopifyOrder(id: number) {
  return (await api.post(`/shopify/orders/${id}/unhold`)).data;
}
export async function resolveShopifySku(sku: string, itemId: number) {
  return (await api.post<ResolveSkuResult>('/shopify/sku-map/resolve', { sku, itemId })).data;
}
export async function getShopifyStockPreview(search?: string) {
  return (await api.get<StockPreview>('/shopify/stock/preview', { params: search ? { search } : {} })).data;
}
export async function pushShopifyStock() {
  return (await api.post<StockPushResult>('/shopify/stock/push', {}, { timeout: 180000 })).data;
}
export async function setShopifySafety(sku: string, safetyStock: number | null) {
  return (await api.patch<{ sku: string; safetyStock: number | null }>('/shopify/stock/safety', { sku, safetyStock })).data;
}
export async function getShopifyUnmapped(search?: string) {
  return (await api.get<UnmappedResult>('/shopify/sku-map/unmapped', { params: search ? { search } : {} })).data;
}
export async function bulkResolveShopifySkus(mappings: { sku: string; itemId: number }[]) {
  return (await api.post<BulkResolveResult>('/shopify/sku-map/resolve-bulk', { mappings }, { timeout: 120000 })).data;
}
/** Send a processed online order that was waiting for stock to the Pick List now. */
export async function retryShopifyPickList(id: number) {
  return (await api.post<RetryPickListResult>(`/shopify/orders/${id}/retry-pick-list`)).data;
}
